import type { AxiosResponse } from "axios";
import { baseUrlApiWithoutTenant } from "../../commons/RootURL";
import { HttpClientNormal } from "../entities/HttpClient";
import { HttpResponse } from "../entities/AxiosMethod";
import { UserInfo } from "../login-service/entities/UserLoginEntity";
import { CreateCustomGroupsRequest, CustomGroup, DeleteCustomGroupsRequest, GlobalConfigurations } from "./Model";

export class SettingService {
  private _httpClient: HttpClientNormal = new HttpClientNormal();
  private _url: string = `${baseUrlApiWithoutTenant}group-calendars/settings`;
  private _userListUrl: string = `${baseUrlApiWithoutTenant}group-calendars/users/list`;
  private _customGroupsUrl: string = `${baseUrlApiWithoutTenant}group-calendars/custom-groups`;
  private _defaultGroupUrl: string = `${baseUrlApiWithoutTenant}group-calendars/settings/group`;
  private _displayGroupUrl: string = `${baseUrlApiWithoutTenant}group-calendars/settings/groupids`;

  async setGroupOrder(groupOrders: string): Promise<HttpResponse<any>> {
    try {
      const response: AxiosResponse = await this._httpClient.post(
        `${this._url}/group-order`,
        groupOrders
      );

      return new HttpResponse<any>(response, undefined);
    } catch (error) {
      console.error("Error setting group order:", error);
      return new HttpResponse<any>(undefined);
    }
  }

  async saveSettings(settings: any): Promise<HttpResponse<any>> {
    try {
      const response: AxiosResponse = await this._httpClient.post(this._url, settings);
      return new HttpResponse<any>(response, undefined);
    } catch (error) {
      console.error("Error saving settings:", error);
      return new HttpResponse<any>(undefined);
    }
  }

  async getUserList(): Promise<HttpResponse<UserInfo[]>> {
    try {
      const response: AxiosResponse = await this._httpClient.get(this._userListUrl);
      let userList: UserInfo[] = [];

      if (response?.data) {
        if (Array.isArray(response.data)) {
          userList = response.data.map((item: any) => new UserInfo(item));
        } else if (
          response.data.groupCalendarUser &&
          Array.isArray(response.data.groupCalendarUser)
        ) {
          userList = response.data.groupCalendarUser.map((item: any) => new UserInfo(item));
        }
      }

      return new HttpResponse<UserInfo[]>(
        { ...response, data: userList } as AxiosResponse,
        undefined
      );
    } catch (error) {
      console.error("Error fetching user list:", error);
      return new HttpResponse<UserInfo[]>(undefined);
    }
  }

  async getCustomGroups(): Promise<HttpResponse<CustomGroup[]>> {
    try {
      const response: AxiosResponse = await this._httpClient.get(this._customGroupsUrl);
      const customGroups: CustomGroup[] = Array.isArray(response?.data)
        ? response.data.map((item: any) => ({
            id: item.id || "",
            name: item.name || item.groupName || "",
            description: item.description || "",
            groupCalendarUserIds: item.groupCalendarUserIds || [],
            members: Array.isArray(item.members)
              ? item.members.map((member: any) => new UserInfo(member))
              : [],
            ...item
          }))
        : [];
      return new HttpResponse<CustomGroup[]>(
        { ...response, data: customGroups } as AxiosResponse,
        undefined
      );
    } catch (error) {
      console.error("Error fetching custom groups:", error);
      return new HttpResponse<CustomGroup[]>(undefined);
    }
  }

  async createCustomGroups(request: CreateCustomGroupsRequest): Promise<HttpResponse<any>> {
    try {
      const response: AxiosResponse = await this._httpClient.post(this._customGroupsUrl, request);
      return new HttpResponse<any>(response, undefined);
    } catch (error) {
      console.error("Error creating custom groups:", error);
      return new HttpResponse<any>(undefined);
    }
  }

  async deleteCustomGroups(request: DeleteCustomGroupsRequest): Promise<HttpResponse<any>> {
    try {
      const response: AxiosResponse = await this._httpClient.delete(this._customGroupsUrl, {
        data: request
      });
      return new HttpResponse<any>(response, undefined);
    } catch (error) {
      console.error("Error deleting custom groups:", error);
      return new HttpResponse<any>(undefined);
    }
  }

  async saveDefaultGroup(defaultGroup: string): Promise<HttpResponse<any>> {
    try {
      const response: AxiosResponse = await this._httpClient.post(
        `${this._defaultGroupUrl}`,
        defaultGroup
      );
      return new HttpResponse<any>(response, undefined);
    } catch (error) {
      console.error("Error saving default group:", error);
      return new HttpResponse<any>(undefined);
    }
  }

  async saveDisplayGroup(groupIds: string[]): Promise<HttpResponse<any>> {
    try {
      const response: AxiosResponse = await this._httpClient.post(
        `${this._displayGroupUrl}`,
        groupIds
      );
      return new HttpResponse<any>(response, undefined);
    } catch (error) {
      return new HttpResponse<any>(undefined);
    }
  }

  async getGlobalConfigurations(): Promise<HttpResponse<GlobalConfigurations>> {
    try {
      const response: AxiosResponse = await this._httpClient.get(
        `${baseUrlApiWithoutTenant}group-calendars/settings/global-configurations`
      );
      return new HttpResponse<GlobalConfigurations>(response, undefined);
    } catch (error) {
      console.error("Error fetching global configurations:", error);
      return new HttpResponse<GlobalConfigurations>(undefined);
    }
  }
}
